import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, GripHorizontal, Crop as CropIcon } from 'lucide-react';
import CropModal from '../ui/CropModal';

const MAX_AUTO_HEIGHT = 560;

// `indent` is how far the photo sits from the column edge it's floated
// against (dragged freehand via the horizontal grip, not a fixed preset) —
// it's what lets a writer put a floated photo anywhere along that edge
// instead of only flush against it.
const floatStyle = (align, width, indent = 0) => {
  if (align === 'left') {
    return { float: 'left', margin: `0 1.25rem 0.75rem ${indent}px`, maxWidth: width ? `${width}px` : '45%' };
  }
  if (align === 'right') {
    return { float: 'right', margin: `0 ${indent}px 0.75rem 1.25rem`, maxWidth: width ? `${width}px` : '45%' };
  }
  return {};
};

const AlignButton = ({ icon: Icon, active, onClick, title }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer',
      background: active ? '#FDE3D7' : 'transparent', color: active ? '#C85A2E' : '#5C7383',
    }}
  >
    <Icon size={14} />
  </button>
);

function ResizableImageView({ node, updateAttributes, selected }) {
  const { src, alt, width, align, indent } = node.attrs;
  const imgRef = useRef(null);
  const autoFitted = useRef(false);
  const [cropping, setCropping] = useState(false);

  // A freshly-inserted photo (especially a tall portrait one) can dominate
  // the whole page before the writer gets a chance to resize it. Scale it
  // down to a sane default height once, the first time it loads — a manual
  // resize (width gets set) always wins over this from then on.
  const autoFit = useCallback((imgEl) => {
    if (autoFitted.current || width || !imgEl?.naturalWidth || !imgEl?.naturalHeight) return;
    autoFitted.current = true;
    if (imgEl.naturalHeight > MAX_AUTO_HEIGHT) {
      updateAttributes({ width: Math.round(imgEl.naturalWidth * (MAX_AUTO_HEIGHT / imgEl.naturalHeight)) });
    }
  }, [width, updateAttributes]);

  useEffect(() => {
    if (imgRef.current?.complete) autoFit(imgRef.current);
  }, [autoFit]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = imgRef.current?.offsetWidth || imgRef.current?.naturalWidth || 300;

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(80, Math.round(startWidth + delta));
      updateAttributes({ width: newWidth });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [updateAttributes]);

  // Drag horizontally from the grip below the photo to put a floated image
  // anywhere along the column — not just flush left or flush right. Which
  // side it floats to flips live as the cursor crosses the column's
  // midpoint; `indent` carries how far it sits from that edge.
  const startPositionDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const column = imgRef.current?.closest('.ProseMirror') || imgRef.current?.parentElement;
    if (!column) return;
    const colRect = column.getBoundingClientRect();
    const maxIndent = colRect.width * 0.6;

    const onMove = (moveEvent) => {
      const x = moveEvent.clientX;
      const mid = colRect.left + colRect.width / 2;
      if (x < mid) {
        const newIndent = Math.min(maxIndent, Math.max(0, Math.round(x - colRect.left)));
        updateAttributes({ align: 'left', indent: newIndent });
      } else {
        const newIndent = Math.min(maxIndent, Math.max(0, Math.round(colRect.right - x)));
        updateAttributes({ align: 'right', indent: newIndent });
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [updateAttributes]);

  const wrapperStyle = align
    ? { position: 'relative', width: width ? `${width}px` : 'auto', ...floatStyle(align, width, indent) }
    : { display: 'inline-block', position: 'relative', maxWidth: '100%', width: width ? `${width}px` : 'auto' };

  return (
    <NodeViewWrapper as="span" style={wrapperStyle}>
      {selected && (
        <span
          style={{
            position: 'sticky', top: 8, zIndex: 10, insetInlineStart: 0,
            display: 'flex', width: 'fit-content', gap: 2, padding: 2, marginBottom: 6,
            background: 'white', borderRadius: 8, border: '1px solid #E8EBEA',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          }}
        >
          <AlignButton icon={AlignCenter} active={!align} title="Full width" onClick={() => updateAttributes({ align: null, indent: 0 })} />
          <AlignButton icon={AlignLeft} active={align === 'left'} title="Wrap text to the right" onClick={() => updateAttributes({ align: 'left', indent: 0 })} />
          <AlignButton icon={AlignRight} active={align === 'right'} title="Wrap text to the left" onClick={() => updateAttributes({ align: 'right', indent: 0 })} />
          <span style={{ width: 1, background: '#E8EBEA', margin: '2px 2px' }} />
          <AlignButton icon={CropIcon} active={cropping} title="Crop photo" onClick={() => setCropping(true)} />
        </span>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt || ''}
        draggable
        data-drag-handle
        onLoad={(e) => autoFit(e.target)}
        style={width ? { width: `${width}px`, maxWidth: '100%', height: 'auto', display: 'block', cursor: 'grab' } : { maxWidth: '100%', height: 'auto', display: 'block', cursor: 'grab' }}
        className={`rounded-lg my-4 ${selected ? 'ring-2 ring-coral-400' : ''}`}
      />
      {selected && (
        <>
          <span onMouseDown={startResize} title="Drag to resize" style={{ position: 'absolute', right: -6, top: 6, width: 14, height: 14, borderRadius: '50%', background: '#fb7f67', border: '2px solid white', cursor: 'nesw-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          <span onMouseDown={startResize} title="Drag to resize" style={{ position: 'absolute', right: -6, bottom: 10, width: 14, height: 14, borderRadius: '50%', background: '#fb7f67', border: '2px solid white', cursor: 'nwse-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          {align && (
            <span
              onMouseDown={startPositionDrag}
              title="Drag to position"
              style={{
                position: 'absolute', left: '50%', bottom: -14, transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 16, borderRadius: 8,
                background: '#fb7f67', border: '2px solid white',
                cursor: 'ew-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', zIndex: 5,
              }}
            >
              <GripHorizontal size={10} color="white" />
            </span>
          )}
        </>
      )}
      {cropping && (
        <CropModal
          src={src}
          onCancel={() => setCropping(false)}
          onConfirm={(newSrc) => {
            autoFitted.current = false;
            updateAttributes({ src: newSrc, width: null });
            setCropping(false);
          }}
        />
      )}
    </NodeViewWrapper>
  );
}

const ResizableImage = Image.extend({
  draggable: true,
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width, style: `width: ${attrs.width}px` } : {}),
        parseHTML: (el) => {
          const fromStyle = el.style.width ? parseInt(el.style.width, 10) : null;
          const fromAttr = el.getAttribute('width') ? parseInt(el.getAttribute('width'), 10) : null;
          return fromStyle || fromAttr || null;
        },
      },
      align: {
        default: null,
        renderHTML: (attrs) => {
          const style = floatStyle(attrs.align, attrs.width, attrs.indent);
          if (!style.float) return {};
          return { style: `float: ${style.float}; margin: ${style.margin}; max-width: ${style.maxWidth};` };
        },
        parseHTML: (el) => (el.style.float === 'left' || el.style.float === 'right' ? el.style.float : null),
      },
      indent: {
        default: 0,
        renderHTML: () => ({}),
        parseHTML: (el) => {
          const side = el.style.float === 'left' ? el.style.marginLeft : el.style.float === 'right' ? el.style.marginRight : null;
          const px = side ? parseInt(side, 10) : 0;
          return Number.isFinite(px) ? px : 0;
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

export default ResizableImage;
